import type { Artwork } from '../types'

type LegacyArtwork = Artwork & { image?: string }

const RESPONSIVE_WIDTHS = [240, 320, 400, 480, 640, 800, 1200, 1600] as const
const DEFAULT_SIZES =
    '(max-width: 640px) 90vw, (max-width: 1024px) 45vw, (max-width: 1440px) 30vw, 400px'

function parseImageUrl(url: string | null | undefined) {
    if (!url) return null
    const [path, queryPart = ''] = url.split('?')
    const lastSlash = path.lastIndexOf('/')
    const lastDot = path.lastIndexOf('.')
    if (lastDot === -1 || lastDot < lastSlash) {
        return null
    }
    const ext = path.slice(lastDot)
    const basePrefix = path.slice(0, lastDot).replace(/_w\d+$/, '')
    return {
        prefix: basePrefix,
        ext,
        query: queryPart ? `?${queryPart}` : '',
    }
}

function buildVariantUrl(parsed: { prefix: string; ext: string; query: string }, width: number) {
    return `${parsed.prefix}_w${width}${parsed.ext}${parsed.query}`
}

export function buildArtworkImageSources(imageUrl: string | null | undefined) {
    const fallbackOriginal = imageUrl ?? ''
    const parsed = parseImageUrl(imageUrl)
    if (!parsed) {
        return {
            src: fallbackOriginal,
            original: fallbackOriginal,
            srcSet: '',
            sizes: DEFAULT_SIZES,
            variants: {},
        }
    }

    const srcSet = RESPONSIVE_WIDTHS.map((width) => `${buildVariantUrl(parsed, width)} ${width}w`).join(', ')

    return {
        src: buildVariantUrl(parsed, 640),
        original: fallbackOriginal || `${parsed.prefix}${parsed.ext}${parsed.query}`,
        srcSet,
        sizes: DEFAULT_SIZES,
        variants: {
            w240: buildVariantUrl(parsed, 240),
            w320: buildVariantUrl(parsed, 320),
            w400: buildVariantUrl(parsed, 400),
            w480: buildVariantUrl(parsed, 480),
            w640: buildVariantUrl(parsed, 640),
            w800: buildVariantUrl(parsed, 800),
            w1200: buildVariantUrl(parsed, 1200),
            w1600: buildVariantUrl(parsed, 1600),
        },
    }
}

export function getArtworkImages(artwork: Artwork): string[] {
    if (Array.isArray(artwork.images) && artwork.images.length > 0) {
        return artwork.images.filter((src): src is string => typeof src === 'string' && src.length > 0)
    }
    const fallback =
        artwork.imageUrl ||
        (artwork as LegacyArtwork).image ||
        ''
    return fallback ? [fallback] : []
}

export function getPrimaryArtworkImage(artwork: Artwork): string | undefined {
    return getArtworkImages(artwork)[0]
}

export const ARTWORK_IMAGE_SIZES = DEFAULT_SIZES

export function getArtworkSalePrice(artwork: Artwork): number | null {
    const basePrice = Number(artwork.price)
    if (!Number.isFinite(basePrice) || basePrice <= 0) return null
    const rawSalePrice = Number(artwork.salePrice)
    if (!Number.isFinite(rawSalePrice)) return null
    const hasSaleFlag = typeof artwork.saleEnabled === 'boolean'
    const saleEnabled = hasSaleFlag ? artwork.saleEnabled : true
    if (!saleEnabled) return null
    if (rawSalePrice <= 0 || rawSalePrice >= basePrice) return null
    return rawSalePrice
}

export function getArtworkEffectivePrice(artwork: Artwork): number {
    return getArtworkSalePrice(artwork) ?? artwork.price
}

export function buildResponsiveImageForWidth(
    imageUrl: string | null | undefined,
    options: { targetWidth?: number; fallback?: string; maxOutputWidth?: number } = {},
) {
    const { targetWidth = 640, fallback, maxOutputWidth } = options
    const resolvedUrl = imageUrl ?? fallback ?? ''
    const sources = buildArtworkImageSources(resolvedUrl || fallback)
    const variants = sources.variants || {}
    const variantEntries = Object.entries(variants)
        .map(([key, url]) => {
            const width = Number(key.replace(/\D+/g, ''))
            return Number.isFinite(width) ? { width, url } : null
        })
        .filter((entry): entry is { width: number; url: string } => Boolean(entry))
        .sort((a, b) => a.width - b.width)
    const limitedVariants =
        typeof maxOutputWidth === 'number' && maxOutputWidth > 0
            ? variantEntries.filter((entry) => entry.width <= maxOutputWidth)
            : variantEntries
    const effectiveVariants = limitedVariants.length > 0 ? limitedVariants : variantEntries
    const selected =
        effectiveVariants.find((entry) => entry.width >= targetWidth) || effectiveVariants[effectiveVariants.length - 1]
    const src = selected?.url || sources.src || resolvedUrl || fallback || ''
    const srcSet =
        effectiveVariants.length > 0
            ? effectiveVariants.map(({ url, width }) => `${url} ${width}w`).join(', ')
            : sources.srcSet || undefined
    return {
        src,
        srcSet,
        sizes: sources.sizes,
        original: sources.original,
    }
}
